import * as yup from 'yup';
import { Action, action, Thunk, thunk, State, ThunkOn, thunkOn } from 'easy-peasy';
import { StoreState } from './store';
import { VideoPlayerProps } from '../typings';
import pkg from '../../package.json';
import { PlayerError } from '../errors/PlayerError';
import { ErrorCodes } from '../errors/ErrorCodes';

export interface ConfigState {
  loaded: boolean;
  version: string;
  videoGuid: string | null;
  userGuid: string | null;
  entityId: string | null;
  recommendationId: string | null;
  language: string | null;
  autoplay: boolean;
  isBODi: boolean;
  extraHeartbeatParams: VideoPlayerProps['extraHeartbeatParams'] | null;
  getAccessToken: VideoPlayerProps['getAccessToken'] | null;
  closeCallback: (() => any) | null;
  onPlayCallback: (() => any) | null;
  onPauseCallback: (() => any) | null;
  onSeekCallback: ((timecode: number) => any) | null;
  onProgressCallback: ((timecode: number) => any) | null;
  onAnyActionCallback: ((action: { type: string; payload: any }) => any) | null;
  cocoaApiUrl: string | null;
  cocoaApiKey: string | null;
  videoApiRetryAttempts: number;
  vhsApiUrl: string | null;
  vhsApiKey: string | null;
  convivaCustomerKey: string | null;
  chromecastAppId: string | null;
  playbackControlsEnabled: boolean;
  debug: boolean;
  logging: boolean;
  useBodwebHeader: boolean;
  simulateVideoApiAuthorizer: boolean;
  skipResume: boolean;
  maxBitrateForEdge: number | null;

  setLoaded: Action<ConfigState, boolean>;
  setVideoGuid: Action<ConfigState, string | null>;
  setUserGuid: Action<ConfigState, string | null>;
  setEntityId: Action<ConfigState, string | null>;
  setRecommendationId: Action<ConfigState, string | null>;
  setLanguage: Action<ConfigState, string | null>;
  setAutoplay: Action<ConfigState, boolean>;
  setIsBODi: Action<ConfigState, boolean>;
  setExtraHeartbeatParams: Action<ConfigState, ConfigState['extraHeartbeatParams']>;
  setGetAccessToken: Action<ConfigState, (() => Promise<{ accessToken: string; tokenType: string }>) | null>;
  setCloseCallback: Action<ConfigState, (() => any) | null>;
  setOnPlayCallback: Action<ConfigState, (() => any) | null>;
  setOnPauseCallback: Action<ConfigState, (() => any) | null>;
  setOnSeekCallback: Action<ConfigState, ((timecode: number) => any) | null>;
  setOnProgressCallback: Action<ConfigState, ((timecode: number) => any) | null>;
  setOnAnyActionCallback: Action<ConfigState, ConfigState['onAnyActionCallback']>;
  setCocoaApiUrl: Action<ConfigState, string | null>;
  setCocoaApiKey: Action<ConfigState, string | null>;
  setVideoApiRetryAttempts: Action<ConfigState, number>;
  setVhsApiUrl: Action<ConfigState, string | null>;
  setVhsApiKey: Action<ConfigState, string | null>;
  setConvivaCustomerKey: Action<ConfigState, string | null>;
  setChromecastAppId: Action<ConfigState, string | null>;
  setPlaybackControlsEnabled: Action<ConfigState, boolean>;
  setDebug: Action<ConfigState, boolean>;
  setLogging: Action<ConfigState, boolean>;
  setUseBodwebHeader: Action<ConfigState, boolean>;
  setSimulateVideoApiAuthorizer: Action<ConfigState, boolean>;
  setSkipResume: Action<ConfigState, boolean>;
  setMaxBitrateForEdge: Action<ConfigState, number | null>;
  load: Thunk<ConfigState, VideoPlayerProps, any, StoreState>;
  reset: Action<ConfigState>;
  onPlayerDestroy: ThunkOn<ConfigState, any, StoreState>;
}

const initialState: State<ConfigState> = {
  loaded: false,
  version: pkg.version,
  videoGuid: null,
  userGuid: null,
  entityId: null,
  recommendationId: null,
  language: null,
  autoplay: true,
  isBODi: false,
  extraHeartbeatParams: null,
  getAccessToken: null,
  closeCallback: null,
  onPlayCallback: null,
  onPauseCallback: null,
  onSeekCallback: null,
  onProgressCallback: null,
  onAnyActionCallback: null,
  cocoaApiUrl: null,
  cocoaApiKey: null,
  videoApiRetryAttempts: 1,
  vhsApiUrl: null,
  vhsApiKey: null,
  convivaCustomerKey: null,
  chromecastAppId: null,
  playbackControlsEnabled: true,
  debug: false,
  logging: false,
  useBodwebHeader: true,
  simulateVideoApiAuthorizer: false,
  skipResume: false,
  maxBitrateForEdge: null,
};

export const configState: ConfigState = {
  /*================
   * State      
   ================*/
  ...initialState,

  /*================
   * Actions      
   ================*/

  load: thunk((actions, videoPlayerProps, { getStoreActions, getStoreState }) => {
    try {
      configValidationSchema.validateSync(videoPlayerProps);
      actions.setVideoGuid(videoPlayerProps.videoGUID);
      actions.setUserGuid(videoPlayerProps.userGUID || null);
      actions.setEntityId(videoPlayerProps.entityId || null);
      actions.setRecommendationId(videoPlayerProps.recommendationId || null);
      actions.setLanguage(videoPlayerProps.language || 'en_US');
      actions.setAutoplay(videoPlayerProps.autoplay ?? true);
      actions.setIsBODi(videoPlayerProps.isBODi ?? false);
      actions.setExtraHeartbeatParams(videoPlayerProps.extraHeartbeatParams || null);
      actions.setGetAccessToken(videoPlayerProps.getAccessToken || null);
      actions.setCloseCallback(videoPlayerProps.closeCallback || null);
      actions.setOnPlayCallback(videoPlayerProps.onPlay || null);
      actions.setOnPauseCallback(videoPlayerProps.onPause || null);
      actions.setOnSeekCallback(videoPlayerProps.onSeek || null);
      actions.setOnProgressCallback(videoPlayerProps.onProgress || null);
      actions.setOnAnyActionCallback(videoPlayerProps.onAnyAction || null);
      actions.setCocoaApiUrl(videoPlayerProps.config.cocoaApiUrl || null);
      actions.setCocoaApiKey(videoPlayerProps.config.cocoaApiKey || null);
      actions.setVideoApiRetryAttempts(videoPlayerProps.config.videoApiRetryAttempts ?? 1);
      actions.setVhsApiUrl(videoPlayerProps.config.vhsApiUrl);
      actions.setVhsApiKey(videoPlayerProps.config.vhsApiKey);
      actions.setConvivaCustomerKey(videoPlayerProps.config.convivaCustomerKey ?? '');
      actions.setChromecastAppId(videoPlayerProps.config.chromecastAppId ?? null);
      actions.setPlaybackControlsEnabled(videoPlayerProps.config.playbackControlsEnabled ?? true);
      actions.setDebug(videoPlayerProps.config.debug ?? false);
      actions.setLogging(videoPlayerProps.config.logging ?? false);
      actions.setUseBodwebHeader(videoPlayerProps.config.useBodwebHeader ?? true);
      actions.setSimulateVideoApiAuthorizer(videoPlayerProps.config.simulateVideoApiAuthorizer ?? false);
      actions.setSkipResume(videoPlayerProps.config.skipResume ?? false);
      actions.setLoaded(true);
      actions.setMaxBitrateForEdge(videoPlayerProps.config.maxBitrateForEdge || null);

      if (videoPlayerProps.actionsRef) {
        const storeActions = getStoreActions();
        videoPlayerProps.actionsRef(storeActions);
      }

      console.info('[Video Player] (Config) Loaded', getStoreState().config);
    } catch (error) {
      throw new PlayerError(ErrorCodes.CONFIGURATION);
    }
  }),

  reset: action(state => {
    Object.assign(state, { ...initialState });
  }),

  setLoaded: action((state, payload) => {
    state.loaded = payload;
  }),

  setVideoGuid: action((state, payload) => {
    state.videoGuid = payload;
  }),

  setUserGuid: action((state, payload) => {
    state.userGuid = payload;
  }),

  setEntityId: action((state, payload) => {
    state.entityId = payload;
  }),

  setRecommendationId: action((state, payload) => {
    state.recommendationId = payload;
  }),

  setLanguage: action((state, payload) => {
    state.language = payload;
  }),

  setAutoplay: action((state, payload) => {
    state.autoplay = payload;
  }),

  setIsBODi: action((state, payload) => {
    state.isBODi = payload;
  }),

  setExtraHeartbeatParams: action((state, payload) => {
    state.extraHeartbeatParams = payload;
  }),

  setGetAccessToken: action((state, payload) => {
    state.getAccessToken = payload;
  }),

  setCloseCallback: action((state, payload) => {
    state.closeCallback = payload;
  }),

  setOnPlayCallback: action((state, payload) => {
    state.onPlayCallback = payload;
  }),

  setOnPauseCallback: action((state, payload) => {
    state.onPauseCallback = payload;
  }),

  setOnSeekCallback: action((state, payload) => {
    state.onSeekCallback = payload;
  }),

  setOnProgressCallback: action((state, payload) => {
    state.onProgressCallback = payload;
  }),

  setOnAnyActionCallback: action((state, payload) => {
    state.onAnyActionCallback = payload;
  }),

  setCocoaApiUrl: action((state, payload) => {
    state.cocoaApiUrl = payload;
  }),

  setCocoaApiKey: action((state, payload) => {
    state.cocoaApiKey = payload;
  }),

  setVideoApiRetryAttempts: action((state, payload) => {
    state.videoApiRetryAttempts = payload;
  }),

  setVhsApiUrl: action((state, payload) => {
    state.vhsApiUrl = payload;
  }),

  setVhsApiKey: action((state, payload) => {
    state.vhsApiKey = payload;
  }),

  setConvivaCustomerKey: action((state, payload) => {
    state.convivaCustomerKey = payload;
  }),

  setChromecastAppId: action((state, payload) => {
    state.chromecastAppId = payload;
  }),

  setPlaybackControlsEnabled: action((state, payload) => {
    state.playbackControlsEnabled = payload;
  }),

  setDebug: action((state, payload) => {
    state.debug = payload;
  }),

  setLogging: action((state, payload) => {
    state.logging = payload;
  }),

  setUseBodwebHeader: action((state, payload) => {
    state.useBodwebHeader = payload;
  }),

  setSimulateVideoApiAuthorizer: action((state, payload) => {
    state.simulateVideoApiAuthorizer = payload;
  }),

  setSkipResume: action((state, payload) => {
    state.skipResume = payload;
  }),

  setMaxBitrateForEdge: action((state, payload) => {
    state.maxBitrateForEdge = payload;
  }),

  onPlayerDestroy: thunkOn(
    function observer(_, storeActions) {
      return [storeActions.player.destroy];
    },
    function handler(actions) {
      actions.reset();
    }
  ),
};

const configValidationSchema = yup.object().shape<VideoPlayerProps>({
  userGUID: yup.string(),
  entityId: yup.string(),
  recommendationId: yup.string().nullable(),
  videoGUID: yup.string().required(),
  autoplay: yup.bool(),
  language: yup.string(),
  getAccessToken: yup.mixed<() => Promise<any>>(),
  closeCallback: yup.mixed<() => any>().test({
    name: 'isOptionalFunction',
    exclusive: false,
    test: (value: any) => ['function', 'undefined'].includes(typeof value),
  }),
  config: yup
    .object()
    .required()
    .shape({
      vhsApiUrl: yup.string().required(),
      vhsApiKey: yup.string().required(),
      convivaCustomerKey: yup.string().required(),
      chromecastAppId: yup.string(),
      debug: yup.boolean(),
      logging: yup.boolean(),
      simulateVideoApiAuthorizer: yup.boolean(),
    }),
});
